import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { getAccountsBloodTests } from "@/lib/api";
import { CsvButton, Empty, Stat, TH, TR, downloadCsv, errMsg, money } from "@/components/accounts/shared";

// Blood tests sold per gym in the month: each 6WC (or Annual) sold with blood tests is two tests.
// Cost is the country's blood test cost per member (Global Admin → Incentives).
export default function BloodTests({ month, country, gym }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    setData(null);
    getAccountsBloodTests({ month, country: country || undefined, gym: gym || undefined })
      .then(setData).catch((e) => toast.error(errMsg(e, "Could not load blood tests")));
  }, [month, country, gym]);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const rows = data.gyms || [];
  if (!rows.length) return <div className="card-surface"><Empty>No gyms with blood tests match.</Empty></div>;
  const members = rows.reduce((a, r) => a + r.members, 0);
  const csv = () => downloadCsv(`blood-tests-${month}.csv`, ["Gym", "Date", "Member", "Programme", "Sold by", "Tests", "Cost"],
    rows.flatMap((r) => r.items.map((it) => [r.gym, it.date, it.member, it.programme, it.rep, data.tests_per_member, r.cost_per_member])));
  return (
    <div className="space-y-4" data-testid="accounts-blood-tests">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="grid grid-cols-2 gap-2 min-w-[280px]">
          <Stat label="Members with blood tests" value={members} />
          <Stat label="Tests" value={members * data.tests_per_member} sub={`${data.tests_per_member} per member`} />
        </div>
        <CsvButton onClick={csv} disabled={!members} testid="blood-tests-csv" />
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium text-right">Members</th>
            <th className="px-3 py-2 font-medium text-right">Tests</th><th className="px-3 py-2 font-medium text-right">Cost to us</th>
            <th className="px-3 py-2 font-medium">By programme</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.facility_id}>
                <tr className={`${TR} cursor-pointer`} onClick={() => setOpen((o) => ({ ...o, [r.facility_id]: !o[r.facility_id] }))} data-testid={`blood-tests-row-${r.facility_id}`}>
                  <td className="px-3 py-2 text-zinc-100">
                    {open[r.facility_id] ? <ChevronDown className="inline h-3.5 w-3.5 mr-1" /> : <ChevronRight className="inline h-3.5 w-3.5 mr-1" />}{r.gym}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.members}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.tests}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.cost_per_member ? money(r, r.cost) : <span className="text-zinc-500">cost not set</span>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{Object.entries(r.by_programme).map(([p, n]) => `${p} ${n}`).join(" · ") || "—"}</td>
                </tr>
                {open[r.facility_id] && (r.items.length ? r.items.map((it) => (
                  <tr key={it.id} className="text-xs text-zinc-400 border-t border-[#1a1a1a]">
                    <td className="px-3 py-1.5 pl-9">{it.date} · {it.member}</td>
                    <td className="px-3 py-1.5 text-right" colSpan={2}>{it.programme}</td>
                    <td className="px-3 py-1.5 text-right">{r.cost_per_member ? money(r, r.cost_per_member) : ""}</td>
                    <td className="px-3 py-1.5">{it.rep ? `Sold by ${it.rep}` : ""}</td>
                  </tr>
                )) : <tr><td colSpan={5} className="px-3 py-2 pl-9 text-xs text-zinc-500">None this month.</td></tr>)}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
